/**
 * manager-header.js
 * Gerencia dropdowns de navegação desktop e o estado visual do header.
 * O drawer mobile segue sendo gerenciado pelo DrawerManager.
 * Disponível como window.HeaderManager.
 */

class HeaderManager {
  constructor(options = {}) {
    this._config = {
      containerSelector: '[data-header]',
      triggerSelector: '[data-dropdown-trigger]',
      menuSelector: '[data-dropdown-menu]',
      openDelay: 150,
      closeDelay: 200,
      openClass: 'is-open',
      activeClass: 'is-active',
      solidClass: 'is-solid',
      transparentClass: 'is-transparent',
      desktopMq: '(min-width: 990px)',
      topOffset: 4,
      ...options,
    };

    this._container = document.querySelector(this._config.containerSelector);
    this._timers = new Map();
    this._activeMenu = null;
    this._isHovered = false;
    this._isTransparentHomeEnabled = this._container?.dataset.homeTransparent === 'true';
    this._isHomeTemplate = this._container?.dataset.homeTemplate === 'true';

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);

    this._init();
    this._bindGlobalEvents();
    this._updateHeaderAppearance();
  }

  _init() {
    if (!this._container) return;

    this._container.querySelectorAll(this._config.triggerSelector).forEach((trigger) => {
      const name = trigger.dataset.dropdownTrigger;
      const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
      if (!menu) return;

      trigger.addEventListener('mouseenter', () => this._scheduleOpen(name));
      trigger.addEventListener('mouseleave', () => this._scheduleClose(name));

      const hoverTarget = menu.querySelector('[data-dropdown-content]') || menu;
      hoverTarget.addEventListener('mouseenter', () => this._cancelClose(name));
      hoverTarget.addEventListener('mouseleave', () => this._scheduleClose(name));

      trigger.addEventListener('focus', () => this._scheduleOpen(name));
      trigger.addEventListener('blur', () => this._scheduleClose(name));
    });

    this._container.addEventListener('mouseenter', this._onMouseEnter);
    this._container.addEventListener('mouseleave', this._onMouseLeave);

    window.eventBus?.on('drawer:open', ({ name }) => {
      if (name !== 'search' && name !== 'cart' && name !== 'menu') return;
      this.closeDropdowns();
      this._updateHeaderAppearance();
    });

    window.eventBus?.on('drawer:close', () => this._updateHeaderAppearance());
  }

  _bindGlobalEvents() {
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click', this._onClick);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);
  }

  _onKeyDown(event) {
    if (event.key === 'Escape') this.closeDropdowns();
  }

  _onClick(event) {
    if (
      !event.target.closest(this._config.triggerSelector) &&
      !event.target.closest(this._config.menuSelector)
    ) {
      this.closeDropdowns();
    }
  }

  _onScroll() {
    this._updateHeaderAppearance();
  }

  _onResize() {
    this._updateHeaderAppearance();
  }

  _onMouseEnter() {
    if (!this._isDesktop()) return;
    this._isHovered = true;
    this._updateHeaderAppearance();
  }

  _onMouseLeave() {
    this._isHovered = false;
    this._updateHeaderAppearance();
  }

  _scheduleOpen(name) {
    this._cancelClose(name);
    const timer = setTimeout(() => this._open(name), this._config.openDelay);
    this._timers.set(`open:${name}`, timer);
  }

  _scheduleClose(name) {
    this._cancelOpen(name);
    const timer = setTimeout(() => this._close(name), this._config.closeDelay);
    this._timers.set(`close:${name}`, timer);
  }

  _cancelOpen(name) {
    clearTimeout(this._timers.get(`open:${name}`));
    this._timers.delete(`open:${name}`);
  }

  _cancelClose(name) {
    clearTimeout(this._timers.get(`close:${name}`));
    this._timers.delete(`close:${name}`);
  }

  _open(name) {
    if (this._activeMenu && this._activeMenu !== name) {
      this._close(this._activeMenu);
    }

    const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.add(this._config.openClass);
    menu.removeAttribute('aria-hidden');
    trigger?.classList.add(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'true');
    this._activeMenu = name;
    this._updateHeaderAppearance();

    window.eventBus?.emit('dropdown:open', { name, menu, trigger });
  }

  _close(name) {
    const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.remove(this._config.openClass);
    menu.setAttribute('aria-hidden', 'true');
    trigger?.classList.remove(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'false');

    if (this._activeMenu === name) this._activeMenu = null;
    this._updateHeaderAppearance();

    window.eventBus?.emit('dropdown:close', { name, menu });
  }

  _isDesktop() {
    return window.matchMedia(this._config.desktopMq).matches;
  }

  _isAtTop() {
    return window.scrollY <= this._config.topOffset;
  }

  _updateHeaderAppearance() {
    if (!this._container) return;

    const searchDrawer = document.getElementById('searchDrawer') || document.getElementById('search_drawer');
    const searchDrawerHidden = searchDrawer ? searchDrawer.classList.contains('hidden') : true;

    if (!this._isTransparentHomeEnabled || !this._isHomeTemplate || !searchDrawerHidden) {
      this._container.classList.add(this._config.solidClass);
      this._container.classList.remove(this._config.transparentClass);
      return;
    }

    const isDesktop = this._isDesktop();
    const isAtTop = this._isAtTop();
    const shouldSolid = isDesktop
      ? !isAtTop || this._isHovered || Boolean(this._activeMenu)
      : !isAtTop;

    this._container.classList.toggle(this._config.solidClass, shouldSolid);
    this._container.classList.toggle(this._config.transparentClass, !shouldSolid);
  }

  closeDropdowns() {
    if (this._activeMenu) this._close(this._activeMenu);
  }

  destroy() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click', this._onClick);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);

    if (this._container) {
      this._container.removeEventListener('mouseenter', this._onMouseEnter);
      this._container.removeEventListener('mouseleave', this._onMouseLeave);
    }

    this._timers.forEach((timer) => clearTimeout(timer));
    this._timers.clear();
  }
}

window.HeaderManager = new HeaderManager();

const input_value = document.querySelectorAll("#searchDrawerInput")

input_value.forEach((inputSearch) => {
  inputSearch.addEventListener('keydown', (e) => {
    if (e.key === "Enter") {
      e.preventDefault()

      const value = e.target.value
      window.location.href = `/search?type=product&q=${value}`
    }
  })
})